import { useState } from "react";
import { Info, Loader2, MapPin } from "lucide-react";

import {
  type CarrierRouteLocation,
  type CarrierTripDetail,
  useComputeCarrierTripRoute,
  useUpdateCarrierTripRoutingDetails,
  useCarrierDispatchCenters,
} from "@/api/carrier";
import { AddressSearch } from "@/components/address-search";
import { centerLatLng } from "./center-location";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { responseError } from "./trip-routing-feedback";
import { isTripRoutingEditable } from "./trip-routing-state";

/**
 * Route setup, as two dialogs.
 *
 * These were two panels sitting open above the stops on every focus, whether
 * or not anyone was planning a route: a datetime field, a Compute button and
 * two coordinate forms, permanently occupying the top of the dialog. Setup is
 * setup — it belongs behind a control, so the default view is the trip.
 *
 * Shaped after the admin console's own route dialogs so the two read the same:
 * tabbed start/end with a switch apiece, and a date plus a 24-hour time.
 *
 * Each dialog reports its own failure inline and keeps what was typed. A form
 * that empties itself on a rejected save costs the dispatcher the coordinates
 * they just read off another screen.
 */

interface RouteLocationDraft {
  enabled: boolean;
  lat: string;
  lng: string;
  address: string;
}

class RouteInputError extends Error {}

const emptyLocationDraft: RouteLocationDraft = {
  enabled: false,
  lat: "",
  lng: "",
  address: "",
};

function routeAddressText(value: unknown): string {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  const address = value as Record<string, unknown>;
  return [
    address.line1,
    address.line2,
    address.city,
    address.province ?? address.state,
    address.postalCode,
    address.country,
  ]
    .filter((part): part is string => typeof part === "string" && !!part)
    .join(", ");
}

function locationDraft(value: unknown, enabled: boolean): RouteLocationDraft {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { ...emptyLocationDraft, enabled };
  }
  const location = value as Record<string, unknown>;
  return {
    enabled,
    lat: typeof location.lat === "number" ? String(location.lat) : "",
    lng: typeof location.lng === "number" ? String(location.lng) : "",
    address: routeAddressText(location.address),
  };
}

function parsedLocation(
  draft: RouteLocationDraft,
  label: string
): CarrierRouteLocation | null {
  if (!draft.enabled) return null;
  const lat = Number(draft.lat);
  const lng = Number(draft.lng);
  if (
    !draft.lat.trim() ||
    !draft.lng.trim() ||
    !Number.isFinite(lat) ||
    lat < -90 ||
    lat > 90 ||
    !Number.isFinite(lng) ||
    lng < -180 ||
    lng > 180
  ) {
    throw new RouteInputError(
      `${label} requires valid latitude and longitude.`
    );
  }
  return {
    lat,
    lng,
    ...(draft.address.trim() && { address: draft.address.trim() }),
  };
}

/** Local date and 24-hour time to an ISO instant, or null if unusable. */
function routeStartInstant(
  date: string,
  time: string
): { iso: string } | { error: string } | null {
  if (!date && !time) return null;
  if (!date || !time) {
    return { error: "Enter both a date and a time, or leave both empty." };
  }

  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) {
    return { error: "Enter a valid time in 24-hour format (HH:MM)." };
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return { error: "Enter a valid time in 24-hour format (HH:MM)." };
  }

  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) {
    return { error: "Enter a valid date." };
  }

  const at = new Date(year, month - 1, day, hours, minutes, 0, 0);
  if (Number.isNaN(at.getTime())) return { error: "Enter a valid date." };
  return { iso: at.toISOString() };
}

export function RouteStartTimeDialog({
  detail,
  open,
  onOpenChange,
  reportSuccess,
}: {
  detail: CarrierTripDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportSuccess: (message: string) => void;
}) {
  const computeRoute = useComputeCarrierTripRoute();
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [error, setError] = useState<string | null>(null);
  const editable = isTripRoutingEditable(detail.status);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleComputeRoute = async () => {
    setError(null);
    const parsed = routeStartInstant(date, time);
    if (parsed && "error" in parsed) {
      setError(parsed.error);
      return;
    }

    try {
      await computeRoute.mutateAsync({
        tripId: detail.id,
        ...(parsed && { startTime: parsed.iso }),
      });
      reportSuccess("Route and ETAs updated.");
      onOpenChange(false);
    } catch (caught) {
      setError(
        responseError(
          caught,
          "Route could not be computed. Check stop locations."
        )
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Set Route Start Time"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleComputeRoute}
            disabled={
              !editable ||
              computeRoute.isPending ||
              detail.waypoints.length === 0
            }
          >
            {computeRoute.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            Apply
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-zinc-500">
          Choose when the trip should start. This affects ETA calculations.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <label
            htmlFor="dispatch-route-start-date"
            className="space-y-1 text-sm font-medium"
          >
            <span>Date</span>
            <Input
              aria-label="Route start date"
              id="dispatch-route-start-date"
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              disabled={!editable || computeRoute.isPending}
            />
          </label>
          <label
            htmlFor="dispatch-route-start-time"
            className="space-y-1 text-sm font-medium"
          >
            <span>Time (24h)</span>
            <Input
              aria-label="Route start time"
              id="dispatch-route-start-time"
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              disabled={!editable || computeRoute.isPending}
            />
          </label>
        </div>

        <p className="flex items-center gap-2 text-sm text-zinc-500">
          <Info className="h-4 w-4 shrink-0" />
          All times are in your local timezone ({timezone}). Leave both empty to
          compute from now.
        </p>

        {detail.waypoints.length === 0 && (
          <p className="text-sm text-amber-700">
            Add a located stop before computing a route.
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

export function RouteSettingsDialog({
  detail,
  open,
  onOpenChange,
  reportSuccess,
}: {
  detail: CarrierTripDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportSuccess: (message: string) => void;
}) {
  const saveRouting = useUpdateCarrierTripRoutingDetails();
  const { data: centers } = useCarrierDispatchCenters();
  const center = centers?.find((item) => item.id === detail.dispatchCenterId);
  const centerLocation = center ? centerLatLng(center) : null;
  const centerAddress = routeAddressText(center?.address);
  const [startDraft, setStartDraft] = useState<RouteLocationDraft>(() =>
    locationDraft(detail.startLocation, detail.useCustomStartLocation)
  );
  const [endDraft, setEndDraft] = useState<RouteLocationDraft>(() =>
    locationDraft(detail.endLocation, detail.useCustomEndLocation)
  );
  const [error, setError] = useState<string | null>(null);
  const editable = isTripRoutingEditable(detail.status);

  const handleSaveRouting = async () => {
    setError(null);
    try {
      const startLocation = parsedLocation(startDraft, "Custom start");
      const endLocation = parsedLocation(endDraft, "Custom end");
      await saveRouting.mutateAsync({
        tripId: detail.id,
        useCustomStartLocation: startDraft.enabled,
        startLocation,
        useCustomEndLocation: endDraft.enabled,
        endLocation,
      });
      reportSuccess("Route endpoints saved.");
      onOpenChange(false);
    } catch (caught) {
      // The dialog stays open and the drafts stay put: a rejected save must
      // not cost the dispatcher coordinates they just typed.
      setError(
        caught instanceof RouteInputError
          ? caught.message
          : responseError(caught, "Route endpoints could not be saved.")
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Trip Route Settings"
      className="sm:max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSaveRouting}
            disabled={!editable || saveRouting.isPending}
          >
            {saveRouting.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            Save Changes
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-zinc-500">
          Configure custom start and end locations for route calculation
        </p>

        <Tabs defaultValue="start">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="start">Start Location</TabsTrigger>
            <TabsTrigger value="end">End Location</TabsTrigger>
          </TabsList>

          <TabsContent value="start">
            <RouteLocationFields
              kind="start"
              draft={startDraft}
              onChange={setStartDraft}
              disabled={!editable || saveRouting.isPending}
              center={
                center
                  ? {
                      name: center.name,
                      address: centerAddress,
                      location: centerLocation,
                    }
                  : null
              }
            />
          </TabsContent>
          <TabsContent value="end">
            <RouteLocationFields
              kind="end"
              draft={endDraft}
              onChange={setEndDraft}
              disabled={!editable || saveRouting.isPending}
            />
          </TabsContent>
        </Tabs>

        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function RouteLocationFields({
  kind,
  draft,
  onChange,
  disabled,
  center,
}: {
  kind: "start" | "end";
  draft: RouteLocationDraft;
  onChange: (value: RouteLocationDraft) => void;
  disabled: boolean;
  center?: {
    name: string;
    address: string;
    location: { lat: number; lng: number } | null;
  } | null;
}) {
  const lat = Number(draft.lat);
  const lng = Number(draft.lng);
  const located =
    draft.enabled &&
    draft.lat.trim() !== "" &&
    draft.lng.trim() !== "" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng);
  const atCenter =
    kind === "start" &&
    located &&
    center?.location?.lat === lat &&
    center?.location?.lng === lng;

  return (
    <div className="space-y-4 pt-3">
      <div className="flex items-center justify-between gap-3">
        <label
          htmlFor={`dispatch-custom-${kind}`}
          className="flex items-center gap-2 text-sm font-medium"
        >
          <Info className="h-4 w-4 text-zinc-400" />
          {kind === "start"
            ? "Use selected start address"
            : "Use custom end location"}
        </label>
        <Switch
          aria-label={
            kind === "start" ? "Use selected start address" : "Use custom end"
          }
          id={`dispatch-custom-${kind}`}
          checked={draft.enabled}
          disabled={disabled}
          onCheckedChange={(checked) =>
            onChange({ ...draft, enabled: checked })
          }
        />
      </div>

      {kind === "start" && !draft.enabled && (
        <p className="text-sm text-zinc-500">
          This trip starts at its first stop. Choose an address to change it.
        </p>
      )}
      {kind === "start" && center?.location && (
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() =>
            onChange({
              enabled: true,
              lat: String(center.location?.lat),
              lng: String(center.location?.lng),
              address: center.address,
            })
          }
        >
          Reset to dispatch center
        </Button>
      )}
      {kind === "start" && !center?.location && (
        <p className="text-sm text-amber-700">
          Set a geocoded address for this trip’s dispatch center to use it as
          the start.
        </p>
      )}

      {draft.enabled && (
        <>
          {kind === "start" && (
            <div className="space-y-2">
              {atCenter ? (
                <p className="text-sm">
                  Dispatch center: {center?.name} — {center?.address}
                </p>
              ) : (
                <p className="text-sm">
                  {located
                    ? `Start: ${draft.address}`
                    : "Choose a start address."}
                </p>
              )}
              <AddressSearch
                disabled={disabled}
                onLoadingChange={() => {}}
                onSelect={(address) =>
                  onChange({
                    enabled: true,
                    lat: String(address.location.lat),
                    lng: String(address.location.lng),
                    address: routeAddressText(address),
                  })
                }
              />
            </div>
          )}
          {kind === "end" && (
            <div className="grid grid-cols-2 gap-2">
              <Input
                aria-label={`Custom ${kind} latitude`}
                inputMode="decimal"
                placeholder="Latitude"
                value={draft.lat}
                disabled={disabled}
                onChange={(event) =>
                  onChange({ ...draft, lat: event.target.value })
                }
              />
              <Input
                aria-label={`Custom ${kind} longitude`}
                inputMode="decimal"
                placeholder="Longitude"
                value={draft.lng}
                disabled={disabled}
                onChange={(event) =>
                  onChange({ ...draft, lng: event.target.value })
                }
              />
            </div>
          )}
          {kind === "end" && (
            <Input
              aria-label={`Custom ${kind} address`}
              placeholder="Address (optional)"
              value={draft.address}
              disabled={disabled}
              onChange={(event) =>
                onChange({ ...draft, address: event.target.value })
              }
            />
          )}

          {located && (
            <div className="rounded-md bg-zinc-50 p-3">
              <div className="mb-1.5 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-zinc-500" />
                <h4 className="text-sm font-medium capitalize">
                  Selected {kind} location
                </h4>
              </div>
              {kind === "end" && (
                <p className="font-mono text-xs tabular-nums text-zinc-600">
                  {lat.toFixed(6)}, {lng.toFixed(6)}
                </p>
              )}
              {draft.address.trim() && (
                <p className="text-xs text-zinc-600">{draft.address.trim()}</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
